import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Download, Inbox, Mail, Phone, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminLeads() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/leads");
    setLeads(data.leads);
  }, []);

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) {
      nav("/results", { replace: true });
      return;
    }
    if (user && user.role === "admin") load();
  }, [user, load, nav]);

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = `${BACKEND}/api/admin/leads/export`;
    a.download = "talentswipe-leads.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  if (!leads) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <span className="nb-badge bg-brand-purple mb-2"><ShieldCheck className="w-4 h-4" /> Admin</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-black">Recruiter Leads</h1>
          <p className="font-semibold text-slate-600 mt-1">{leads.length} lead{leads.length === 1 ? "" : "s"} captured</p>
        </div>
        {leads.length > 0 && (
          <button data-testid="export-leads-btn" onClick={exportCsv} className="nb-btn bg-brand-yellow flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>

      {leads.length === 0 ? (
        <div className="nb-card p-10 bg-brand-yellow text-center">
          <Inbox className="w-10 h-10 mx-auto mb-3" />
          <p className="font-heading text-xl font-bold">No leads yet</p>
          <p className="font-semibold text-black/70">Leads from "Connect with a Recruiter" will show up here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {leads.map((l, i) => (
            <motion.div
              key={l.id || i}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`lead-card-${i}`}
              className="nb-card p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-heading text-xl font-bold text-black">{l.name}</h3>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <a href={`mailto:${l.email}`} className="nb-badge bg-white"><Mail className="w-3.5 h-3.5" /> {l.email}</a>
                <span className="nb-badge bg-white"><Phone className="w-3.5 h-3.5" /> {l.phone}</span>
              </div>
              {l.message && (
                <p className="text-sm font-semibold text-black/80 border-2 border-black rounded-xl bg-brand-yellow px-3 py-2 mb-3">{l.message}</p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {(l.top_matches || []).map((m, j) => (
                  <span key={j} className="text-xs font-bold bg-black text-white rounded-full px-3 py-1">
                    {m.title} · {m.match_score}%
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
